import { useLocation } from "@solidjs/router";
import clsx from "clsx";
import {
  Component,
  Match,
  type ParentComponent,
  Show,
  Switch,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";
import { isServer } from "solid-js/web";
import { InterstitialGeneratingViewer } from "~/components/chat/messages/r/InterstitialGeneratingViewer";
import { InterstitialViewer } from "~/components/chat/messages/r/InterstitialViewer";
import {
  ExtendedPaneContentType,
  ExtendedPaneMode,
  NavigationPaneMode,
  isExtendedPaneVisible,
  useAppUI,
} from "./AppUIContext";
import { ExtendedPaneArtifactViewer } from "./ExtendedPaneArtifactViewer";
import { ExtendedPaneConceptsPowering } from "./ExtendedPaneConceptsPowering";

export const ExtendedPane: ParentComponent = (props) => {
  const { state, actions } = useAppUI();
  const location = useLocation();
  const [isResizing, setIsResizing] = createSignal(false);
  const [isMobile, setIsMobile] = createSignal(false);
  const [isNavigating, setIsNavigating] = createSignal(false);

  let resizeHandleRef: HTMLDivElement | undefined;
  let panelRef: HTMLDivElement | undefined;

  // Track location changes to prevent auto-resize during navigation
  createEffect(() => {
    const pathname = location.pathname;
    setIsNavigating(true);

    const timeout = setTimeout(() => {
      setIsNavigating(false);
    }, 300); // Match transition timing

    onCleanup(() => clearTimeout(timeout));
  });

  // Auto-size to 50% of available space when first opened or viewport changes
  createEffect(() => {
    if (isServer || isResizing() || isNavigating()) return; // Don't auto-resize during manual resize or navigation

    const handleResize = () => {
      if (
        isExtendedPaneVisible(state.extendedPane) &&
        !isResizing() &&
        !isNavigating() &&
        !state.extendedPane.hasBeenManuallyResized
      ) {
        // Use viewport width for calculations (decoupled from NavigationPane)
        const viewportWidth = window.innerWidth;
        const targetWidth = viewportWidth * 0.4; // 40% of viewport

        // Only auto-resize if current width is significantly different from target
        const currentWidth = state.extendedPane.width;
        const widthDifference = Math.abs(currentWidth - targetWidth);

        // Update if difference is more than 50px to avoid constant adjustments
        if (widthDifference > 50) {
          actions.setExtendedPaneWidth(targetWidth);
        }
      }
    };

    // Initial sizing
    handleResize();

    // Listen for window resize
    window.addEventListener("resize", handleResize);

    onCleanup(() => {
      window.removeEventListener("resize", handleResize);
    });
  });

  // Resize functionality
  const handleResizeStart = (e: MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startWidth = state.extendedPane.width;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      const deltaX = e.clientX - startX;
      const newWidth = startWidth - deltaX; // Right side panel, dragging left (negative deltaX) increases width, dragging right (positive deltaX) decreases width

      // Apply width change immediately without batching for responsive feel
      requestAnimationFrame(() => {
        actions.setExtendedPaneWidth(newWidth, true); // Mark as manual resize
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove, { capture: true });
      document.removeEventListener("mouseup", handleMouseUp, { capture: true });
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.body.style.pointerEvents = "";
    };

    document.addEventListener("mousemove", handleMouseMove, { capture: true });
    document.addEventListener("mouseup", handleMouseUp, { capture: true });
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.body.style.pointerEvents = "none"; // Prevent interference from other elements
  };

  const handleToggle = () => {
    actions.toggleExtendedPane();
  };

  // Detect mobile viewport for full-screen overlay
  // Below 640px, disable resizing and use full-width overlay
  createEffect(() => {
    if (isServer) return;

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    onCleanup(() => {
      window.removeEventListener("resize", checkMobile);
    });
  });

  // ESC key handler to close panel
  onMount(() => {
    if (isServer) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isVisible()) {
        actions.setExtendedPaneMode(ExtendedPaneMode.HIDDEN);
      }

      // Simple focus trap: Tab key cycles within panel when visible
      if (e.key === "Tab" && isVisible() && panelRef) {
        const focusableElements = panelRef.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        const focusableArray = Array.from(focusableElements) as HTMLElement[];

        if (focusableArray.length === 0) return;

        const firstElement = focusableArray[0];
        const lastElement = focusableArray[focusableArray.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    onCleanup(() => {
      window.removeEventListener("keydown", handleKeyDown);
    });
  });

  const isVisible = () => isExtendedPaneVisible(state.extendedPane);

  // Panel width - full screen on mobile, configurable on desktop
  const paneWidth = () => {
    if (isMobile()) return window.innerWidth;
    return state.extendedPane.width;
  };

  const activeContent = createMemo(() => {
    if (state.extendedPane.conceptsPoweringChat) {
      return {
        type: ExtendedPaneContentType.CONCEPTS_POWERING_CHAT,
      };
    }
    // Show the single active artifact if available
    return state.extendedPane.activeArtifact;
  });

  // Render the appropriate viewer based on content type
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const renderViewer = (content: any) => {
    if (!content) return null;

    const contentType = content.type || ExtendedPaneContentType.ARTIFACT; // Default to artifact for backwards compatibility

    switch (contentType) {
      case ExtendedPaneContentType.INTERSTITIAL:
        return <InterstitialViewer operationId={content.operationId} blockId={content.blockId} chat={content.chat} />;
      case ExtendedPaneContentType.INTERSTITIAL_GENERATING:
        return (
          <InterstitialGeneratingViewer
            operationId={content.operationId}
            blockId={content.blockId}
            chat={content.chat}
          />
        );
      case ExtendedPaneContentType.ARTIFACT:
        return (
          <ExtendedPaneArtifactViewer
            operationId={content.operationId}
            blockId={content.blockId}
            title={content.title}
            editMode={content.editMode}
            mount={panelRef}
          />
        );
      case ExtendedPaneContentType.CONCEPTS_POWERING_CHAT:
        return <ExtendedPaneConceptsPowering />;
      default:
        return (
          <ExtendedPaneArtifactViewer
            operationId={content.operationId}
            blockId={content.blockId}
            title={content.title}
            editMode={content.editMode}
            mount={panelRef}
          />
        );
    }
  };

  const handleBackdropClick = () => {
    actions.setExtendedPaneMode(ExtendedPaneMode.HIDDEN);
  };

  const handlePanelClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handleBackdropWheel = (e: WheelEvent) => {
    // Forward scroll events to the main scrollable content area behind the backdrop
    // This allows users to scroll the chat content while the overlay panel is open
    const scrollableContent = document.querySelector("main .overflow-y-auto");
    if (scrollableContent) {
      scrollableContent.scrollBy({
        top: e.deltaY,
        left: e.deltaX,
        behavior: "instant",
      });
    }
  };

  return (
    <Show when={isVisible()}>
      {/* Transparent clickable overlay - captures clicks to close, allows scroll passthrough */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: transparent overlay for click-outside-to-close, ESC key also closes panel */}
      <div
        class="fixed inset-0 bg-transparent z-[100]"
        onClick={handleBackdropClick}
        onWheel={handleBackdropWheel}
        aria-hidden="true"
      />

      {/* Panel - overlay content */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: onClick only prevents propagation, doesn't trigger action */}
      <aside
        ref={panelRef}
        class="fixed top-0 h-screen flex flex-col bg-canvas shadow-2xl border-l-2 border-l-brand/30 z-[101] transition-transform duration-200"
        classList={{
          "translate-x-full": !isVisible(),
          "translate-x-0": isVisible(),
        }}
        style={{
          width: `${paneWidth()}px`,
          right: "0px",
        }}
        onClick={handlePanelClick}
        aria-label="Extended pane"
        role="dialog"
        aria-modal="true"
      >
        {/* Simplified content area - no header, no borders */}
        <div class="flex-1 h-full">
          <Switch>
            {/* Priority order: children > active content > static content > fallback */}
            <Match when={props.children}>{props.children}</Match>
            <Match when={activeContent()}>{renderViewer(activeContent())}</Match>
            <Match when={state.extendedPane.content}>{state.extendedPane.content}</Match>
            <Match when={true}>
              <div class="text-center py-8">
                <div class="text-secondary-text">
                  <p class="text-sm">No content to display</p>
                </div>
              </div>
            </Match>
          </Switch>
        </div>

        {/* Resize handle - hidden on mobile */}
        <Show when={!isMobile()}>
          <div
            ref={resizeHandleRef}
            class={clsx(
              "absolute top-0 h-full w-2 cursor-col-resize transition-colors group -left-3",
              isResizing() ? "bg-brand/20" : "bg-transparent hover:bg-brand/10",
            )}
            onMouseDown={handleResizeStart}
            aria-label="Resize extended pane"
          >
            {/* Border highlight */}
            <div
              class={clsx(
                "absolute top-0 h-full w-1 transition-colors left-2",
                isResizing() ? "bg-brand" : "bg-transparent group-hover:bg-brand/50",
              )}
            />
            {/* Visual drag indicator */}
            <div
              class={clsx(
                "absolute top-1/2 -translate-y-1/2 w-1 h-20 rounded-full transition-colors left-2",
                isResizing() ? "bg-brand" : "bg-transparent group-hover:bg-brand",
              )}
            />
          </div>
        </Show>
      </aside>
    </Show>
  );
};
